import store, { AllState } from '../../../store'
import { PageArea, PageProps } from '../index'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGridContext } from '../../containers/BulkSheetView/hooks/useGridContext'
import ProxyRequestHeader from './components/Header'
import { useProxyRequestData } from './hooks/proxyRequestData'
import { usePageState } from './hooks/pageState'
import { DateTerm } from '../../../utils/date'
import {
  ProxyRequestRow,
  hasWorkingHours,
  StandardWorkingInfo,
} from './ProxyRequest'
import {
  actualWorkingHoursGridOptions,
  refreshDynamicColumnDef,
  updateDisplayProjectColumn,
} from './gridOptions'
import { connect } from 'react-redux'
import { InputError } from '../../containers/BulkSheetView/lib/validation'
import {
  MessageLevel,
  addGlobalMessage,
  addScreenMessage,
} from '../../../store/messages'
import { intl } from '../../../i18n'
import { getRowNumber } from '../../containers/BulkSheetView/lib/gridApi'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'
import { SearchUnit } from '../../components/headers/HeaderBar/DateRangeSelector'
import { useTaskActualWorkTaskDialogState } from './hooks/taskActualWorkTaskDialogState'
import { useExcelExport } from './hooks/excelExport'
import { RouteComponentProps } from 'react-router'
import { SelectOptionWithIcon } from '../../components/select/model'
import ProxyRequestToolbar from './components/Toolbar'
import { ToolbarToggleValue } from '../../components/toolbars/Toolbar/ToolbarToggle'
import { Collapse } from '@mui/material'
import { useAssignedProjectsData } from '../ActualWorkingHours/hooks/assignedProjectsData'
import { SplitButtonOptions } from '../../components/buttons/SplitButton'
import AlertDialog from '../../components/dialogs/AlertDialog'
import { useAlertDialogState } from './hooks/useAlertDialogState'
import SavePopper from '../../containers/BulkSheetView/components/header/SaveButtonArea'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import { useProjectPrivateContext } from '../../context/projectContext'
import { FunctionLayer } from '../../../store/functionLayer'
import {
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent,
} from 'ag-grid-community'
import { BulkSheetView } from '../../containers/BulkSheetView'
import Loading from '../../components/process-state-notifications/Loading'
import CancelConfirmDialog from '../../components/dialogs/CancelConfirmDialog'
import TaskActualWorkTaskDialog from '../../components/dialogs/TaskActualWorkTaskDialog'
import DateVO from '../../../vo/DateVO'
import {
  CustomEnumCode,
  getCustomEnumValues,
} from '../../../lib/functions/customEnumValue'
import { parse } from '../../../lib/commons/i18nLabel'
import { featureFlagService } from '../../../utils/feature-flag'

type PathProps = {
  code?: string
  term?: string
}

type StateProps = {
  functionLayers: Immutable.Map<number, FunctionLayer>
  user
}

type Props = PageProps & RouteComponentProps<PathProps> & StateProps

const TaskActualWorkProxyRequest = projectPrivate((props: Props) => {
  const functionUuid = props.uuid
  const { project } = useProjectPrivateContext()
  const [loading, setLoading] = useState<boolean>(true)
  const [userUuid, setUserUuid] = useState<string | undefined>()
  const [userName, setUserName] = useState<string>('')
  const [selectedMember, setSelectedMember] = useState<
    SelectOptionWithIcon<string> | undefined
  >(undefined)
  const [openCancel, setOpenCancel] = useState<boolean>(false)
  const [toolbar, setToolbar] = useState<ToolbarToggleValue | undefined>(
    undefined
  )
  const [reloadHeader, setReloadHeader] = useState<boolean>(false)
  const [standardWorkInfo, setStandardWorkInfo] = useState<StandardWorkingInfo>(
    {
      standardWorkDays: 0,
      standardWorkHours: 0,
      totalWorkDays: 0,
      totalWorkHours: 0,
      diffDays: 0,
      diffHours: 0,
      diffDaysPercent: 0,
      diffHoursPercent: 0,
    }
  )
  const {
    data,
    summaryRow,
    ratioRow,
    refresh,
    saveRequest,
    getStandardWorkingInfo,
    updateRequestCheck,
    clearData,
  } = useProxyRequestData(project.uuid)
  const pageState = usePageState({ functionUuid })
  const { taskDialogState, setTaskDialogState, openTaskActualWorkList } =
    useTaskActualWorkTaskDialogState()
  const { alertDialogState, openAlertDialog, handleSubmit, handleClose } =
    useAlertDialogState()
  const { assignedProjects } = useAssignedProjectsData({
    userUuid,
    projectUuid: undefined,
  })

  const callbackCellEdit = useCallback(() => {
    setReloadHeader(true)
  }, [])

  const gridOptions = useMemo(() => {
    return actualWorkingHoursGridOptions({
      editable: true,
      openTaskActualWorkList,
      callbackCellEdit,
    })
  }, [callbackCellEdit, openTaskActualWorkList])

  const gridApiRef = useRef<GridApi>()
  const [isGridReady, setIsGridReady] = useState(false)
  const { contextRef, updateContext } = useGridContext(
    gridApiRef.current,
    gridOptions.context
  )

  const onGridReady = useCallback((event: GridReadyEvent) => {
    gridApiRef.current = event.api
    setIsGridReady(true)
  }, [])

  const reload = useRef<Function>()
  reload.current = refresh

  const prevData = useRef<ProxyRequestRow[]>([])
  prevData.current = data
  const hasChanged = useCallback((): boolean => {
    return (prevData.current || data).some(v => v.added || v.edited)
  }, [data])

  const refreshAll = useCallback(async () => {
    if (!gridApiRef.current) return
    setLoading(true)
    try {
      const refreshRows = reload.current ?? refresh
      await refreshRows(
        userUuid,
        project.uuid,
        pageState.displayDateTerm.startDate,
        pageState.displayDateTerm.endDate
      )
    } finally {
      updateContext({
        errors: new InputError(),
      })
      setReloadHeader(true)
      setLoading(false)
    }
  }, [
    updateContext,
    pageState.displayDateTerm.endDate,
    pageState.displayDateTerm.startDate,
    refresh,
    userUuid,
    project.uuid,
  ])

  const onSubmit = useCallback(async () => {
    if (!userUuid) {
      return
    }
    setLoading(true)
    try {
      gridApiRef.current?.stopEditing()
      if (contextRef.current.errors.hasMessage()) {
        store.dispatch(
          addGlobalMessage({
            type: MessageLevel.WARN,
            title: intl.formatMessage({ id: 'global.warning.businessError' }),
            text: contextRef.current.errors.toMessage(id => {
              const node = gridApiRef.current?.getRowNode(id)
              return !!node ? getRowNumber(node).toString() : ''
            }),
          })
        )
        return
      }
      const requestResponse = await saveRequest(userUuid)

      if (
        requestResponse.json?.cancel &&
        Array.isArray(requestResponse.json.cancel) &&
        requestResponse.json.cancel.length > 0
      ) {
        const failedRequests = requestResponse.json.cancel
          .filter(cancel => cancel.failedRequestStatusUuids?.length > 0)
          .flatMap(cancel => cancel.failedRequestStatusUuids)

        if (failedRequests.length > 0) {
          const matchedData = data.filter(row =>
            row.projectRequestStatus?.some(status =>
              failedRequests.includes(status.requestUuid)
            )
          )
          const failureDetails = matchedData
            .map(row => ({
              date: row.date,
              projects:
                row.projectRequestStatus
                  ?.filter(status =>
                    failedRequests.includes(status.requestUuid!)
                  )
                  .map(
                    status =>
                      assignedProjects?.find(p => p.uuid === status.projectUuid)
                        ?.displayName
                  )
                  .filter(Boolean) || [],
            }))
            .filter(data => data.date && data.projects.length > 0)
            .map(data => {
              const [year, month, day] = data.date!.split('-').map(Number)
              return intl.formatMessage(
                {
                  id: 'actualWorkingHours.application.snackBar.message.failureDetails',
                },
                { month, day, projectDisplayName: data.projects.join(', ') }
              )
            })
            .join('\n')

          const messageText =
            intl.formatMessage({
              id: 'actualWorkingHours.application.snackBar.message',
            }) + (failureDetails ? `\n\n${failureDetails}` : '')

          store.dispatch(
            addGlobalMessage({
              type: MessageLevel.WARN,
              title: intl.formatMessage({ id: 'global.warning.businessError' }),
              text: messageText,
            })
          )
          await refreshAll()
          return
        }
      }
      if (!requestResponse.hasError && !requestResponse.hasWarning) {
        store.dispatch(
          addScreenMessage(functionUuid, {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({ id: 'registration.complete' }),
          })
        )
        await refreshAll()
      }
    } finally {
      setLoading(false)
    }
  }, [data, functionUuid, assignedProjects, refreshAll, saveRequest, userUuid])

  const onReload = useCallback(() => {
    if (hasChanged()) {
      setOpenCancel(true)
    } else {
      refreshAll()
    }
  }, [hasChanged, refreshAll])

  const callbackReload = useCallback(async () => {
    await refreshAll()
  }, [refreshAll])

  const onSearch = useCallback(
    async (unit: SearchUnit, term: DateTerm) => {
      if (unit !== pageState.searchUnit) {
        pageState.setSearchUnit(unit)
      } else if (term !== pageState.displayDateTerm) {
        pageState.setDisplayDateTerm(term)
      }
    },
    [pageState]
  )

  const onCheckRequestUntilToday = useCallback(async () => {
    const today = new Date()
    const todayRows = data.filter(row => {
      if (!row.date) return false
      const rowDate = new Date(row.date)
      return rowDate <= today && hasWorkingHours(row)
    })
    updateRequestCheck(todayRows, true, gridApiRef.current)
  }, [data, updateRequestCheck])

  const onUncheckRequestUntilToday = useCallback(async () => {
    const today = new Date()
    const todayRows = data.filter(row => {
      if (!row.date) return false
      const rowDate = new Date(row.date)
      return rowDate <= today
    })
    updateRequestCheck(todayRows, false, gridApiRef.current)
  }, [data, updateRequestCheck])

  const onExportExcel = useExcelExport(contextRef, gridApiRef.current!, {
    dateTerm: pageState.displayDateTerm,
    userName,
    standardInfo: standardWorkInfo,
  })

  const onChangeToolbar = useCallback(
    (value: ToolbarToggleValue | undefined) => {
      setToolbar(value)
    },
    []
  )

  useKeyBind(
    [{ key: KEY_SAVE, fn: onSubmit, stopDefaultBehavior: true }],
    [data, saveRequest, pageState.displayDateTerm]
  )

  useEffect(() => {
    updateContext({
      errors: new InputError(),
      projectUuid: project?.uuid,
      projectCode: project?.code,
    })
  }, [updateContext, project?.uuid, project?.code])

  useEffect(() => {
    if (!pageState.initialized) return
    setLoading(false)
  }, [pageState.initialized])

  useEffect(() => {
    if (
      !pageState.initialized ||
      !pageState.displayDateTerm ||
      !pageState.displayDateTerm.startDate ||
      !pageState.displayDateTerm.endDate ||
      !userUuid ||
      !selectedMember
    ) {
      return
    }
    refreshAll()
  }, [
    pageState.initialized,
    pageState.displayDateTerm,
    userUuid,
    selectedMember,
    refreshAll,
  ])

  useEffect(() => {
    if (selectedMember) {
      setUserUuid(selectedMember.value)
      setUserName(selectedMember.name)
    } else {
      setUserUuid(undefined)
      setUserName('')
      clearData()
    }
  }, [selectedMember, clearData])

  useEffect(() => {
    updateContext({
      userUuid,
    })
  }, [updateContext, userUuid])

  useEffect(() => {
    if (reloadHeader) {
      const stdInfo = getStandardWorkingInfo()
      setReloadHeader(false)
      setStandardWorkInfo(stdInfo)
    }
  }, [getStandardWorkingInfo, reloadHeader])

  useEffect(() => {
    gridApiRef.current?.resetRowHeights()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.rowHeight])

  const batchMenu = useMemo(() => {
    return [
      {
        key: intl.formatMessage({
          id: 'actualWorkingHours.application.select.all',
        }),
        onClick: onCheckRequestUntilToday,
      },
      {
        key: intl.formatMessage({
          id: 'actualWorkingHours.application.deselect.all',
        }),
        onClick: onUncheckRequestUntilToday,
      },
    ] as SplitButtonOptions[]
  }, [onCheckRequestUntilToday, onUncheckRequestUntilToday])

  // ProxyRequestView logic moved here
  const ref = useRef<HTMLDivElement>(null)
  const contextMenu = useCallback((params: GetContextMenuItemsParams) => {
    return []
  }, [])

  const fetchSelectColumnOptions = useCallback(async () => {
    const response = await getCustomEnumValues({
      customEnumCode: CustomEnumCode.WORKING_HOURS_TYPE,
    })
    const workingHoursType = response.json.map(v => ({
      ...v,
      nameI18n: parse(v.nameI18n),
    }))
    updateContext({
      workingHoursType,
    })
  }, [updateContext])

  const onCancelConfirm = useCallback(() => {
    setOpenCancel && setOpenCancel(false)
    callbackReload()
  }, [callbackReload])

  useEffect(() => {
    fetchSelectColumnOptions()
  }, [fetchSelectColumnOptions])

  useEffect(() => {
    refreshDynamicColumnDef({
      api: gridApiRef.current,
      projects: assignedProjects,
      userUuid,
      openTaskActualWorkList,
      isConfirm: false,
    })
  }, [isGridReady, assignedProjects, userUuid, openTaskActualWorkList])

  useEffect(() => {
    updateDisplayProjectColumn(gridApiRef.current, data)
  }, [isGridReady, data])

  return (
    <>
      <AlertDialog
        isOpen={alertDialogState?.isOpen || false}
        title={alertDialogState?.title}
        message={alertDialogState?.message}
        submitHandler={handleSubmit}
        closeHandler={handleClose}
      />
      <PageArea>
        <SavePopper loading={loading} onSubmit={onSubmit} />
        <ProxyRequestHeader
          onCancel={onReload}
          searchUnit={pageState.searchUnit}
          dateTerm={pageState.displayDateTerm}
          onSearch={onSearch}
          onClickExport={onExportExcel}
          toolbar={toolbar}
          onChangeToolbar={onChangeToolbar}
          rowHeight={pageState.rowHeight}
          onChangeRowHeight={(value: number) => pageState.setRowHeight(value)}
          splitButtonProps={{ options: batchMenu }}
          projectUuid={project.uuid}
          selectedMember={selectedMember}
          setSelectedMember={setSelectedMember}
          loginUser={props.user}
          bulkSheetViewRef={ref}
        />
        <Collapse in={!!toolbar} timeout={100} sx={{ padding: '5px 0' }}>
          <ProxyRequestToolbar
            toolbar={ToolbarToggleValue.DISPLAY}
            data={standardWorkInfo}
          />
        </Collapse>
        <>
          <BulkSheetView
            ref={ref}
            gridOptions={gridOptions}
            rowHeight={pageState.rowHeight}
            rowData={data}
            onGridReady={onGridReady}
            pinnedTopRowData={
              featureFlagService.isEnabled('task-actual-work-application')
                ? [ratioRow, summaryRow]
                : [summaryRow]
            }
            getContextMenuItems={contextMenu}
          />
          <Loading isLoading={loading} elem={ref.current} />
          <CancelConfirmDialog
            open={openCancel ?? false}
            onConfirm={onCancelConfirm}
            onClose={() => setOpenCancel && setOpenCancel(false)}
          />
          {taskDialogState && (
            <TaskActualWorkTaskDialog
              anchorEl={taskDialogState.anchorElement}
              date={new DateVO(taskDialogState.date)}
              actualWorks={taskDialogState.taskActualWorks}
              actualHour={taskDialogState.total}
              event={taskDialogState.clickEvent as PointerEvent}
              onClose={() => setTaskDialogState(null)}
              openDetail={taskDialogState.openDetail}
            />
          )}
        </>
      </PageArea>
    </>
  )
})

const mapStateToProps = (state: AllState) => ({
  functionLayers: state.functionLayer.layers,
  user: state.user.user,
})

export default connect(mapStateToProps)(TaskActualWorkProxyRequest)
